import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import './DateRangePicker.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseIso(iso) {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDisplay(iso) {
  const d = parseIso(iso);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function compareIso(a, b) {
  if (!a || !b) return 0;
  return a.localeCompare(b);
}

function isBetween(iso, from, to) {
  if (!from || !to || !iso) return false;
  return compareIso(iso, from) >= 0 && compareIso(iso, to) <= 0;
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export default function DateRangePicker({
  dateFrom = '',
  dateTo = '',
  onChange,
  placeholder = 'Filter by date',
  ariaLabel = 'Filter by date range',
}) {
  const rootRef = useRef(null);
  const buttonId = useId();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [hoverIso, setHoverIso] = useState('');

  const initialView = useMemo(() => {
    const anchor = parseIso(dateFrom) || parseIso(dateTo) || new Date();
    return { year: anchor.getFullYear(), month: anchor.getMonth() };
  }, [dateFrom, dateTo]);

  const [viewYear, setViewYear] = useState(initialView.year);
  const [viewMonth, setViewMonth] = useState(initialView.month);

  useEffect(() => {
    if (!open) return undefined;
    setViewYear(initialView.year);
    setViewMonth(initialView.month);
    setHoverIso('');
    return undefined;
  }, [open, initialView.year, initialView.month]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayLabel = useMemo(() => {
    if (dateFrom && dateTo) {
      return `${formatDisplay(dateFrom)} – ${formatDisplay(dateTo)}`;
    }
    if (dateFrom) return `${formatDisplay(dateFrom)} – …`;
    return '';
  }, [dateFrom, dateTo]);

  const selectingEnd = Boolean(dateFrom && !dateTo);

  const previewTo = selectingEnd && hoverIso ? hoverIso : dateTo;
  const rangeFrom =
    dateFrom && previewTo && compareIso(previewTo, dateFrom) < 0 ? previewTo : dateFrom;
  const rangeTo =
    dateFrom && previewTo && compareIso(previewTo, dateFrom) < 0 ? dateFrom : previewTo;

  const cells = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const goPrevMonth = useCallback(() => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  }, [viewMonth]);

  const goNextMonth = useCallback(() => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  }, [viewMonth]);

  const handleDayClick = useCallback(
    (iso) => {
      if (!iso) return;
      if (!dateFrom || (dateFrom && dateTo)) {
        onChange?.({ dateFrom: iso, dateTo: '' });
        return;
      }
      let from = dateFrom;
      let to = iso;
      if (compareIso(to, from) < 0) {
        from = iso;
        to = dateFrom;
      }
      onChange?.({ dateFrom: from, dateTo: to });
      setOpen(false);
    },
    [dateFrom, dateTo, onChange],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.({ dateFrom: '', dateTo: '' });
      setOpen(false);
    },
    [onChange],
  );

  const todayIso = toIsoDate(new Date());

  return (
    <div ref={rootRef} className="date-range-picker">
      <button
        id={buttonId}
        type="button"
        className={`date-range-trigger${displayLabel ? '' : ' date-range-trigger--placeholder'}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
      >
        <FiCalendar className="date-range-trigger-icon" aria-hidden="true" />
        <span className="date-range-trigger-label">{displayLabel || placeholder}</span>
        {(dateFrom || dateTo) && (
          <span
            role="button"
            tabIndex={0}
            className="date-range-clear"
            aria-label="Clear date range"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e);
              }
            }}
          >
            <FiX aria-hidden="true" />
          </span>
        )}
      </button>

      {open && (
        <div id={panelId} className="date-range-panel" role="dialog" aria-label={ariaLabel}>
          <div className="date-range-panel-header">
            <button type="button" className="date-range-nav" onClick={goPrevMonth} aria-label="Previous month">
              <FiChevronLeft />
            </button>
            <span className="date-range-month-title">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button type="button" className="date-range-nav" onClick={goNextMonth} aria-label="Next month">
              <FiChevronRight />
            </button>
          </div>
          <p className="date-range-hint">
            {selectingEnd ? 'Select end date' : 'Select start date'}
          </p>
          <div className="date-range-weekdays">
            {WEEKDAYS.map((d) => (
              <span key={d} className="date-range-weekday">
                {d}
              </span>
            ))}
          </div>
          <div className="date-range-grid">
            {cells.map((date, idx) => {
              if (!date) {
                return <span key={`empty-${idx}`} className="date-range-day date-range-day--empty" />;
              }
              const iso = toIsoDate(date);
              const inRange =
                dateFrom && previewTo && isBetween(iso, rangeFrom, rangeTo);
              const isStart = iso === dateFrom;
              const isEnd = iso === (dateTo || (selectingEnd ? hoverIso : ''));
              const isToday = iso === todayIso;

              return (
                <button
                  key={iso}
                  type="button"
                  className={[
                    'date-range-day',
                    inRange ? 'date-range-day--in-range' : '',
                    isStart ? 'date-range-day--start' : '',
                    isEnd ? 'date-range-day--end' : '',
                    isToday ? 'date-range-day--today' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleDayClick(iso)}
                  onMouseEnter={() => {
                    if (selectingEnd) setHoverIso(iso);
                  }}
                  onMouseLeave={() => setHoverIso('')}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
